'use client';
import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { Printer } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import PublicShell from '@/components/public/PublicShell';
import { SitePage, formatLongDate, sitePagesService } from '@/lib/sitePages/sitePagesService';

interface LegalPageProps {
  slug: string;
  fallbackTitle: string;
}

export default function LegalPage({ slug, fallbackTitle }: LegalPageProps) {
  const [page, setPage] = useState<SitePage | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    sitePagesService
      .getBySlug(slug)
      .then((p) => {
        if (cancelled) return;
        setPage(p);
        setFailed(!p);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const effective = formatLongDate(page?.effectiveDate);
  const nextReview = formatLongDate(page?.nextReview);

  return (
    <PublicShell>
      <article className="legal-page bg-[#F8F3E9]">
        <header className="legal-band bg-[#581C32] text-white">
          <div className="mx-auto w-full max-w-[760px] px-4 py-8 sm:py-10">
            <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#C6A667]">
              CARE BUSINESS SCHOOL · BUILD · SUSTAIN · SCALE
            </p>
            <h1 className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">{page?.title || fallbackTitle}</h1>
            {page?.subtitle && <p className="mt-2 text-base leading-relaxed text-white/80">{page.subtitle}</p>}
          </div>
        </header>

        <div className="mx-auto w-full max-w-[760px] px-4 py-8 sm:py-10">
          {loading && <p className="text-sm text-[#2b1a22]/70">Loading…</p>}

          {!loading && failed && (
            <div className="rounded-xl border border-[#C6A667] bg-white p-6">
              <p className="text-base font-semibold text-[#581C32]">This page is not available right now.</p>
              <p className="mt-1 text-sm text-[#2b1a22]/70">Please try again shortly, or email hello@carebusinessschool.co.uk.</p>
              <Link href="/" className="mt-4 inline-flex rounded-lg bg-[#581C32] px-4 py-2.5 text-sm font-semibold text-white">
                Back to home
              </Link>
            </div>
          )}

          {!loading && page && (
            <>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm font-medium text-[#581C32]">
                  {page.version && <>Version {page.version}</>}
                  {page.version && effective && ' · '}
                  {effective && <>Effective {effective}</>}
                  {nextReview && <> · Next review {nextReview}</>}
                </p>
                <button
                  type="button"
                  onClick={() => window.print()}
                  className="no-print inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg border-2 border-[#581C32] bg-white px-4 py-2 text-sm font-bold text-[#581C32] transition-all hover:bg-[#581C32] hover:text-white active:scale-95"
                >
                  <Printer size={16} aria-hidden="true" /> Print or save as PDF
                </button>
              </div>

              <div className="legal-body mt-6 rounded-2xl bg-white p-5 shadow-sm sm:p-8">
                <div className="prose prose-sm max-w-none text-[#2b1a22] sm:prose-base prose-headings:font-bold prose-headings:text-[#581C32] prose-a:text-[#581C32] prose-a:underline prose-strong:text-[#2b1a22] prose-li:marker:text-[#C6A667]">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      h1: ({ children }) => <h2>{children}</h2>,
                      table: ({ children }) => (
                        <div className="my-4 w-full overflow-x-auto">
                          <table>{children}</table>
                        </div>
                      ),
                    }}
                  >
                    {page.bodyMarkdown}
                  </ReactMarkdown>
                </div>
              </div>
            </>
          )}
        </div>
      </article>
    </PublicShell>
  );
}
